import { MatButtonModule } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatFormField, MatFormFieldModule } from "@angular/material/form-field";
import { MatSelect, MatOption, MatSelectModule } from "@angular/material/select";
import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { getDictionaryItem } from "../../../utils/dictionary.domain";
import { MatInput } from "@angular/material/input";

import { FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { FormBuilder } from "@angular/forms";

import { CaseViewModel } from "../../../interfaces/case-view-model";
import { Category } from "../../../interfaces/category.interface";
import { BusinessArea } from "../../../interfaces/business-area-ce.interface";
import { Account } from "../../../interfaces/accounts.interface";
import { CreateCaseService } from "../../../services/create-case.service";
import { BusinessAreaService } from "../../../services/business-area.service";
import { CategoryService } from "../../../services/category.service";
import { AccountsService } from "../../../services/accounts.service";
import { CaseCreated, CaseDetails, CaseState } from "../../../interfaces/case.interface";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ImageGalleryComponent } from "../../image-gallery/image-gallery.component";
import { FileToCase } from "../../../interfaces/fileToCase.interface";
import { MessageCenterComponent } from "../../message-center/message-center.component";
import { MessageBannerState, MessageCenter } from "../../../interfaces/messageCenter.interface";
import { MsalService } from "@azure/msal-angular";
import { UserAccountId, UserContact } from "../../../interfaces/user-info.interface";
import { MessageCenterService } from "../../../services/messageCenter.service";

import { CdkTrapFocus } from "@angular/cdk/a11y";
import { handleClipboardFiles } from "../../../utils/pasteFiles.utils";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTimepickerModule } from "@angular/material/timepicker";

import { DateAdapter, MAT_DATE_FORMATS, provideNativeDateAdapter } from "@angular/material/core";
import { take } from "rxjs";

const DATE_FORMATTING = {
    parse: {
        dateInput: "DD/MM/YYYY"
    },
    display: {
        dateInput: "DD/MM/YYYY",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "DD/MM/YYYY",
        monthYearA11yLabel: "MMMM YYYY"
    }
};

@Component({
    selector: "app-create-case-dialog",
    standalone: true,
    imports: [
        ImageGalleryComponent,
        ReactiveFormsModule,
        MatIcon,
        MatInput,
        MatButtonModule,
        MatFormField,
        MatSelect,
        MatOption,
        MatSelectModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MessageCenterComponent,
        MatDatepickerModule,
        MatTimepickerModule,
        CdkTrapFocus
    ],
    templateUrl: "./create-case-dialog.component.html",
    styleUrl: "./create-case-dialog.component.scss",
    providers: [provideNativeDateAdapter(), { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATTING }]
})
export class CreateCaseDialogComponent {
    @ViewChild("formGroup") formGroup!: HTMLElement;
    @ViewChild("imageGallery") imageGallery!: ImageGalleryComponent;
    @Output() caseState = new EventEmitter<CaseState>();
    @Output() caseCreated = new EventEmitter<CaseCreated>();

    caseForm: FormGroup;

    showCloseWarning: boolean = false;
    showMessageCenter: boolean = false;
    accountid: string = localStorage.getItem("accountid") || "";

    popupId: string = "create-case-popup";
    popupForm: string = "create-case-form";
    popupWarning: string = "show-close-case-warning";
    submitButton: string = "submit-case-button";

    public case?: CaseViewModel;
    public categories?: Category[];
    public businessAreas?: BusinessArea[];
    public accounts?: Account[];
    public accountName?: string;
    public hasAdminRights: boolean = false;
    public caseImages: { title: string; url: string }[] = [];
    public userAccounts: UserAccountId[] = [];

    today: Date = new Date();
    timeOptions: string[] = [];

    public messagePopup: { show: boolean; content?: MessageCenter } = { show: false, content: undefined };
    isSubmitting = false;
    messageCenter: MessageCenter = {
        title: getDictionaryItem("caseErrorText") + " " + getDictionaryItem("caseErrorSumplementalText")
    };
    messageCenterState: MessageBannerState = MessageBannerState.error;

    get categoryIsRequired(): boolean {
        if (this.categories && this.categories.length === 0) {
            return false;
        } else return true;
    }

    constructor(
        public dialogRef: MatDialogRef<CreateCaseDialogComponent>,
        private createCaseService: CreateCaseService,
        private businessAreaService: BusinessAreaService,
        private categoryService: CategoryService,
        private accountService: AccountsService,
        private messagecenterService: MessageCenterService,
        private formBuilder: FormBuilder,
        private authService: MsalService,
        private dateAdapter: DateAdapter<Date>
    ) {
        this.dateAdapter.setLocale("da-DK");

        const activeAccount = this.authService.instance.getActiveAccount();
        const activeEmail = activeAccount?.idTokenClaims?.emails?.[0] || activeAccount?.username || null;

        this.caseForm = this.formBuilder.group({
            account: ["", Validators.required],
            company: [""],
            title: ["", [Validators.required, Validators.minLength(10)]],
            businessArea: ["", Validators.required],
            category: ["", Validators.required],
            description: ["", [Validators.required, Validators.minLength(10)]],
            createdDate: [""],
            createdTime: [""]
        });

        this.accountService.getConnectedAccounts(this.accountid).subscribe((accounts: Account[]) => {
            this.accounts = accounts;
        });

        this.accountService.getAccount(this.accountid).subscribe((account) => {
            this.accountName = account.name;
            this.hasAdminRights = account.hasAdminRights;
        });

        this.accountService.getContactPerson(activeEmail).subscribe((user: UserContact[]) => {
            if (!user.length) return;

            this.userAccounts = user
                .filter((c) => c.hasWebAccess === true)
                .map((c) => ({
                    accountId: c.currentAccount.accountId,
                    contactid: c.contactId,
                    parentcustomerid_account: {
                        accountid: c.currentAccount.accountId,
                        accountnumber: c.currentAccount.accountNumber,
                        name: c.currentAccount.name
                    }
                }));
            this.userAccounts.sort((a, b) => a.parentcustomerid_account.name.localeCompare(b.parentcustomerid_account.name));
        });

        this.businessAreaService.getAreas().subscribe((areas: BusinessArea[]) => {
            this.businessAreas = areas;
            if (!this.hasAdminRights) {
                this.businessAreas = this.businessAreas.filter((area) => area.id !== "3e4fa03d-0568-ee11-9ae7-002248844951");
            }
        }),
            (error: any) => {
                console.error(error);
                this.showMessageCenter = true;
                setTimeout(() => {
                    this.showMessageCenter = false;
                }, 5000);
            };
    }

    ngOnInit() {
        this.caseForm = this.formBuilder.group({
            account: ["", Validators.required],
            company: [""],
            title: ["", [Validators.required, Validators.minLength(10)]],
            businessArea: ["", Validators.required],
            category: ["", Validators.required],
            description: ["", [Validators.required, Validators.minLength(10)]],
            createdDate: [""],
            createdTime: [""]
        });
        setTimeout(() => {
            if (this.userAccounts.length && this.userAccounts.length === 1) {
                this.caseForm.controls["account"].setValue(this.userAccounts[0].parentcustomerid_account.accountid);
            }
            const categoryField = document.getElementById("category-select-field");
            if (categoryField) {
                categoryField.setAttribute("aria-disabled", "true");
                categoryField.setAttribute("tabindex", "-1");
            }
        }, 100);

        this.caseForm.get("createdDate")?.valueChanges.subscribe((newDate) => {
            this.generateTimeOptions(newDate);
            this.caseForm.get("createdTime")?.setValue("");
            if (newDate) {
                this.caseForm.get("createdTime")?.setValidators(Validators.required);
            } else {
                this.caseForm.get("createdTime")?.clearValidators();
            }
            this.caseForm.get("createdTime")?.updateValueAndValidity();
        });
    }

    getDictionaryItem = getDictionaryItem;

    eventHandler(event: { title: string; url: string }[]) {
        this.caseImages = event;
    }

    closeVaultDialog() {
        if (
            this.caseForm.value.businessArea.length ||
            this.caseForm.value.category.length ||
            this.caseForm.value.description.length ||
            this.caseForm.value.title.length ||
            this.caseForm.value.description.length
        ) {
            this.showCloseWarning = true;
        } else this.dialogRef.close();
    }

    clearDateOption() {
        this.caseForm.get("createdDate")?.setValue("");
        this.caseForm.get("createdTime")?.setValue("");
    }

    generateTimeOptions(selectedDate?: Date | null) {
        if (!selectedDate) selectedDate = this.caseForm?.value?.createdDate;
        if (selectedDate && !(selectedDate instanceof Date)) {
            selectedDate = this.dateAdapter.deserialize(selectedDate);
        }

        const now = new Date();

        let startHour = 8;
        let endHour = 15;
        let endMinute = 30;
        let interval = 30;

        if (selectedDate) {
            const isToday = selectedDate.getDate() === now.getDate() && selectedDate.getMonth() === now.getMonth() && selectedDate.getFullYear() === now.getFullYear();

            if (isToday) {
                let next = new Date(now.getTime() + 60 * 60 * 1000);
                next.setSeconds(0, 0);
                if (next.getMinutes() % interval !== 0) {
                    next.setMinutes(next.getMinutes() + (interval - (next.getMinutes() % interval)));
                }
                // Only allow times after both 8:00 and next available time
                if (next.getHours() > startHour || (next.getHours() === startHour && next.getMinutes() > 0)) {
                    startHour = next.getHours();
                    interval = 30;
                }
            }
        }

        const options: string[] = [];
        for (let hour = startHour; hour <= endHour; hour++) {
            for (let min = 0; min < 60; min += interval) {
                // Only allow up to 15:30
                if (hour === endHour && min > endMinute) continue;
                if (selectedDate) {
                    const isToday =
                        selectedDate.getDate() === now.getDate() && selectedDate.getMonth() === now.getMonth() && selectedDate.getFullYear() === now.getFullYear();
                    if (isToday) {
                        let next = new Date(now.getTime() + 60 * 60 * 1000);
                        next.setSeconds(0, 0);
                        if (next.getMinutes() % interval !== 0) {
                            next.setMinutes(next.getMinutes() + (interval - (next.getMinutes() % interval)));
                        }
                        const minHour = next.getHours();
                        const minMinute = next.getMinutes();
                        if (hour < minHour || (hour === minHour && min < minMinute)) continue;
                    }
                }
                const h = hour.toString().padStart(2, "0");
                const m = min.toString().padStart(2, "0");
                options.push(`${h}:${m}`);
            }
        }
        this.timeOptions = options;
    }

    businessAreasEvent() {
        this.getBusinessAreaMessage();
        this.categoryService.getCategories(this.caseForm.value.businessArea ?? "").subscribe((categories: Category[]) => {
            this.categories = categories;

            if (this.categories.length === 0) {
                this.showMessageCenter = true;
                setTimeout(() => {
                    this.showMessageCenter = false;
                }, 5000);
            }

            // Commented out for now, as it is not needed for the current implementation
            /*this.caseForm.controls['category'].setValidators(this.categoryIsRequired ? Validators.required : null);
        this.caseForm.controls['category'].updateValueAndValidity(); */
        });

        if (this.caseForm.value.businessArea !== "") {
            const categoryField = document.getElementById("category-select-field");
            if (categoryField) {
                categoryField.setAttribute("aria-disabled", "false");
                categoryField.setAttribute("tabindex", "0");
            }
        }
    }

    getBusinessAreaMessage() {
        if (!this.caseForm.value.businessArea) return;
        this.messagecenterService.getAllActiveMessages(this.caseForm.value.businessArea).subscribe((messages: MessageCenter[]) => {
            if (!messages.length) return;
            this.messagePopup.show = true;
            // to prevent an overload of messages, only the first message will be shown
            const message = messages[0];
            this.messagePopup.content = message;
            setTimeout(() => {
                document.getElementById("message-popup-close-button")?.focus();
            }, 100);
        });
    }

    submitImagesToCase(imageUrl: string, caseId: string, filename: string) {
        const mappedImages: FileToCase | undefined = this.createCaseService.mapFileToCase(caseId, imageUrl, filename);
        if (mappedImages) {
            this.createCaseService.postFileToCE(mappedImages).subscribe((response) => {
                return response;
            });
        }
    }

    redirectToWorks(account?: string, caseId?: string) {
        if (this.caseForm.value.account !== this.accountid) {
            this.caseState.emit({
                success: true,
                message: `${getDictionaryItem("caseSubmittedText")} for: ${account}! Omdirigere dig til det valgte forsyning...`
            });
            this.messageCenterState = MessageBannerState.success;
            this.caseCreated.emit({ created: true, caseId: caseId ?? "" });
            setTimeout(() => {
                localStorage.setItem("accountid", this.caseForm.value.account);
                window.location.reload();
            }, 5000);
        } else {
            this.caseState.emit({ success: true, message: `${getDictionaryItem("caseSubmittedText")}!` });
            this.caseCreated.emit({ created: true, caseId: caseId ?? "" });
            this.messageCenterState = MessageBannerState.success;
        }
    }

    onSubmit() {
        console.log("Submitting form");
        const reporterId = this.userAccounts.find((account) => account.parentcustomerid_account.accountid === this.caseForm.value.account)?.contactid;
        const accountName = this.userAccounts.find((account) => account.parentcustomerid_account.accountid === this.caseForm.value.account)?.parentcustomerid_account
            .name;

        let caseDetails: CaseDetails = {
            caseId: "",
            title: this.caseForm.value.title ?? "",
            description: this.caseForm.value.description ?? "",
            category: this.caseForm.value.category ?? "",
            businessArea: this.caseForm.value.businessArea ?? "",
            customerId: this.caseForm.value.account ?? "",
            reportedBy: reporterId ?? "",
            caseOriginCode: 3,
            accountid: this.caseForm.value.account ?? "",
            images: []
        };

        if (this.caseForm.value.createdDate && this.caseForm.value.createdTime) {
            let formattedDateTime: string | null = null;

            const date: Date = this.caseForm.value.createdDate;
            const time: string = this.caseForm.value.createdTime;

            const [hours, minutes] = time.split(":").map(Number);
            const combinedDate = new Date(date);
            combinedDate.setHours(hours, minutes, 0, 0);
            formattedDateTime = combinedDate.toISOString();
            caseDetails = { ...caseDetails, dueDate: formattedDateTime };
        }

        this.isSubmitting = true;
        //this.caseForm.disable();

        this.createCaseService
            ?.createCase(caseDetails)
            ?.pipe(take(1))
            .subscribe((response: any) => {
                if (response) {
                    caseDetails.caseId = response.incidentid;
                    if (this.caseImages.length > 0) {
                        this.caseImages.forEach((image: { title: string; url: string }) => {
                            this.submitImagesToCase(image.url, response.incidentid, image.title);
                            this.isSubmitting = false;
                            this.dialogRef.close();

                            this.redirectToWorks(accountName, response.incidentid);
                        });
                    } else {
                        this.isSubmitting = false;
                        this.dialogRef.close();

                        this.redirectToWorks(accountName, response.incidentid);
                    }
                } else {
                    this.caseState.emit({ success: false, message: getDictionaryItem("caseErrorText") });
                    this.isSubmitting = false;
                    console.error("No response");
                }
            });
    }

    async pasteFilesToImageGallery(event: ClipboardEvent) {
        await handleClipboardFiles(event, (syntheticEvent) => {
            if (this.imageGallery) {
                this.imageGallery.selectFiles(syntheticEvent);
            }
        });
    }
}
